import React, { useEffect, useState } from 'react';
import { Button, Message } from '@arco-design/web-react';
import * as dd from 'dingtalk-jsapi';
import { apiDingdingLogin } from '@/api/system';
import { login } from '@/utils/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrg } from '@/utils/hooks/useOrg';
import { useMemoizedFn } from 'ahooks';
import { DINGTALK_CONFIG } from '@/constants/dingtalk';
import styles from './DingTalkLogin.module.less';

interface DingTalkLoginProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const DingTalkLogin: React.FC<DingTalkLoginProps> = ({
  onSuccess,
  onError,
}) => {
  const [isInDingTalk, setIsInDingTalk] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { orgId } = useOrg();

  const { corpId, appId, isValid } = DINGTALK_CONFIG.validateConfig(
    orgId || ''
  );

  const navigateToHome = useMemoizedFn(() => {
    const redirectUrl = searchParams?.get('redirect');
    const defaultPath = `/org/${orgId}/`;
    try {
      const decodeUrl = redirectUrl
        ? decodeURIComponent(redirectUrl)
        : undefined;
      navigate(decodeUrl || defaultPath, { replace: true });
    } catch (e) {
      navigate(defaultPath, { replace: true });
    }
  });

  // 检测钉钉环境
  useEffect(() => {
    const checkDingTalkEnv = () => {
      const inDingTalk = dd.env.platform !== 'notInDingTalk';
      setIsInDingTalk(inDingTalk);
      if (inDingTalk) {
        // 在钉钉内，尝试自动登录
        handleDingTalkAutoLogin();
      }
    };

    checkDingTalkEnv();
  }, []);

  // 钉钉内自动登录
  const handleDingTalkAutoLogin = async () => {
    if (!isValid) {
      onError?.('钉钉配置不完整，请联系管理员');
      return;
    }

    setLoading(true);
    try {
      dd.ready(async () => {
        try {
          const res = await dd.runtime.permission.requestAuthCode({
            corpId: corpId!,
          });

          const { token } = await apiDingdingLogin({
            code: res.code,
          });

          if (token) {
            login(token);
            Message.success('钉钉登录成功');
            onSuccess?.();
            navigateToHome();
          }
        } catch (error) {
          console.error('钉钉自动登录失败:', error);
          onError?.('钉钉自动登录失败，请尝试其他登录方式');
        } finally {
          setLoading(false);
        }
      });
    } catch (error) {
      console.error('钉钉环境初始化失败:', error);
      setLoading(false);
      onError?.('钉钉环境初始化失败');
    }
  };

  // 生成扫码登录二维码
  // const generateQRCode = async () => {
  //   if (!isValid) {
  //     onError?.('钉钉配置不完整，请联系管理员');
  //     return;
  //   }

  //   setLoading(true);
  //   try {
  //     // 由于没有具体的扫码登录API，这里提供一个简化的实现
  //     // 实际项目中需要后端提供相应的接口
  //     setShowQRCode(true);

  //     // 模拟生成二维码的过程
  //     setTimeout(() => {
  //       setLoading(false);
  //       onError?.('扫码登录功能暂未开放，请使用手机号登录');
  //       setShowQRCode(false);
  //     }, 1000);
  //   } catch (error) {
  //     console.error('生成二维码失败:', error);
  //     onError?.('生成二维码失败，请稍后重试');
  //     setLoading(false);
  //   }
  // };

  if (isInDingTalk) {
    return (
      <div className={styles.dingTalkContainer}>
        <Button
          type="primary"
          size="large"
          long
          loading={loading}
          onClick={handleDingTalkAutoLogin}
          className={styles.dingTalkButton}
        >
          钉钉授权登录
        </Button>
      </div>
    );
  }

  if (showQRCode) {
    return (
      <div className={styles.qrCodeContainer}>
        <div className={styles.qrCodeTitle}>钉钉扫码登录</div>
        <div className={styles.qrCodeWrapper}>
          <div className={styles.qrCodeLoading}>功能开发中...</div>
        </div>
        <div className={styles.qrCodeTip}>
          扫码登录功能暂未开放，请使用手机号登录
        </div>
        <Button
          type="text"
          size="small"
          onClick={() => setShowQRCode(false)}
          className={styles.backButton}
        >
          返回
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.dingTalkContainer}>
      <div className={styles.dingTalkTitle}>点击按钮使用钉钉授权登录</div>
      <Button
        type="primary"
        size="large"
        long
        loading={loading}
        onClick={async () => {
          const redirectUrl = searchParams?.get('redirect');
          const callbackUrl = new URL(
            `${window.location.origin}/org/${orgId}/dingtalk-callback`
          );
          if (redirectUrl) {
            callbackUrl.searchParams.set('redirect', redirectUrl);
          }
          callbackUrl.searchParams.set('corp_id', `${corpId}`);
          window.location.href = `https://login.dingtalk.com/oauth2/challenge.htm?prompt=consent&state=&scope=openid&client_id=${appId}&response_type=code&redirect_uri=${encodeURIComponent(
            callbackUrl.href
          )}`;
        }}
        className={styles.dingTalkButton}
      >
        钉钉授权登录
      </Button>
    </div>
  );
};

export default DingTalkLogin;
